import { severityForValue } from "../../../whiteLabel/whiteLabelTheme";

import classes from "./WhiteLabelScoreBar.module.css";

interface Props {
  /** Metric name (backend-localized). */
  title: string;
  /** Score caption, e.g. "79% | Good" (backend-localized). */
  caption?: string;
  value: number;
  /** Band names for the scale beneath the track, or omit to hide them. */
  tickLabels?: string[];
  /** Optional one-line explainer. */
  description?: string;
  /**
   * Renders the two-half risk track instead of the banded fill. `active` selects
   * which half is emphasised — the scale is binary, not a continuum.
   */
  range?: { low: string; high: string; active: "low" | "high" };
  height?: number;
}

/**
 * Condensed score bar for the single-page results list.
 *
 * Not the shared ChartBars: that renders the five PNOE palette colours (all five
 * differ from the white-label severity scale) and, critically, hides the bar
 * entirely unless title2 contains a "%" — which a translated caption may not.
 * Here the track is driven by `value` alone and the caption is pure text.
 */
const WhiteLabelScoreBar = ({
  title,
  caption,
  value,
  tickLabels,
  description,
  range,
  height,
}: Props): JSX.Element => {
  const band = severityForValue(value);
  const width = Math.max(0, Math.min(100, value));

  return (
    <div className={classes.bar} style={height ? { height } : undefined}>
      <div className={classes.title}>
        <span className={classes.name}>{title}</span>
        {caption && <span className={classes.score}> — {caption}</span>}
      </div>

      {description && <div className={classes.description}>{description}</div>}

      {range ? (
        <>
          <div className={classes.rangeTrack}>
            <div
              className={`${classes.rangeHalf} ${
                range.active === "low"
                  ? classes.rangeHalfLow
                  : classes.rangeHalfLowDim
              }`}
            />
            <div
              className={`${classes.rangeHalf} ${
                range.active === "high"
                  ? classes.rangeHalfHigh
                  : classes.rangeHalfHighDim
              }`}
            />
            {/* Centre of the active half. */}
            <div
              className={classes.thumb}
              style={{ left: range.active === "high" ? "75%" : "25%" }}
            />
          </div>
          <div className={classes.rangeLabels}>
            <span
              className={`${classes.rangeLabel} ${
                range.active === "low" ? "" : classes.rangeLabelDim
              }`}
            >
              {range.low}
            </span>
            <span
              className={`${classes.rangeLabel} ${
                range.active === "high" ? "" : classes.rangeLabelDim
              }`}
            >
              {range.high}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className={classes.track}>
            <div
              className={classes.fill}
              style={{ width: `${width}%`, backgroundColor: band.color }}
            />
          </div>

          {tickLabels && tickLabels.length > 0 && (
            <div className={classes.ticks}>
              {tickLabels.map((label) => (
                <span className={classes.tick} key={label}>
                  {label}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WhiteLabelScoreBar;
